"use client";

import React, { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useMasquerade } from "@/contexts/masquerade-context";
import { apiClient, profileApiClient } from "@/lib/axios";
import { queryClient } from "@/lib/queryClient";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, token } = useAuth();
  const { impersonatedWallet, stopMasquerade } = useMasquerade();

  // Impersonation must never outlive the admin's own authenticated session.
  useEffect(() => {
    if (!isAuthenticated && impersonatedWallet) {
      stopMasquerade();
    }
  }, [isAuthenticated, impersonatedWallet, stopMasquerade]);

  useEffect(() => {
    let isMounted = true;

    const syncAuthHeaders = async () => {
      // Use the Privy identity token (which carries linked_accounts) rather than
      // the access token. The backend resolves each user's actual wallet —
      // including external wallets — directly from the token's linked_accounts,
      // instead of relying on an unreliable per-request getUserById lookup that
      // fails for external-wallet users (causing 401 "Wallet not connected").
      const authToken = isAuthenticated ? token : null;

      if (!isMounted) return;

      if (isAuthenticated && authToken) {
        apiClient.defaults.headers["Authorization"] = `Bearer ${authToken}`;
        apiClient.defaults.headers["x-privy-access-token"] = authToken;
        profileApiClient.defaults.headers["x-privy-access-token"] =
          authToken;
      } else {
        delete apiClient.defaults.headers["Authorization"];
        delete apiClient.defaults.headers["x-privy-access-token"];
        delete profileApiClient.defaults.headers["x-privy-access-token"];
      }

      // Admin "view as user" impersonation. Only set on apiClient (the
      // first-party backend that honors X-Act-As, and only for admins). NOT set
      // on profileApiClient: that is the third-party Pulsar EVM API, which
      // resolves identity from the token and would ignore the header — profile
      // reads/writes are gated on !isImpersonating instead (see hooks/profile).
      if (isAuthenticated && authToken && impersonatedWallet) {
        apiClient.defaults.headers["X-Act-As"] = impersonatedWallet;
      } else {
        delete apiClient.defaults.headers["X-Act-As"];
      }

      await new Promise((resolve) => setTimeout(resolve, 150));

      queryClient.invalidateQueries({
        queryKey: [],
      });
    };

    syncAuthHeaders();

    return () => {
      isMounted = false;
    };
  }, [token, isAuthenticated, impersonatedWallet]);

  return <>{children}</>;
};
